import {
  CheckBox, CheckBoxOutlineBlank, Delete, Edit,
  Image as ImageIcon, PermMedia, PlayCircleOutline,
  PlaylistAdd, Upload, Videocam,
} from '@mui/icons-material'
import {
  Box, Button, Checkbox, Chip, CircularProgress, Fab,
  FormControl, IconButton, InputLabel, ListItemIcon, ListItemText,
  Menu, MenuItem, Modal, Select, Skeleton, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import DialogQuiz from '../../Components/Modal/DialogQuiz'
import EmptyState from '../../Components/EmptyState'
import ModalHeader from '../../Components/Modal/ModalHeader'
import { Media } from '../../Model/MediaModel'
import { Playlist } from '../../Model/PlaylistModel'
import { useDeleteMediaMutation, useFindAllMediaQuery } from '../../service/MediaService'
import { useFindAllPlaylistsQuery } from '../../service/PlaylistService'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setMedia } from '../../store/reducers/media'
import { usePagination } from '../../Util/Pagination'
import { resolveMediaUrl } from '../../Util/mediaUrl'
import MediaViewer from './DialogMediaViewer'
import Helper from './Helper'
import ModalAddToPlaylist from './ModalAddToPlaylist'
import ModalUpdate from './ModalUpdate'
import ModalUpload from './ModalUpload'
import { useSharedFile } from '../../hooks/useSharedFile'
import PlaylistQuickPanel from './PlaylistQuickPanel'
import { AppPageHeader, TealButton } from '../../Components/ui'

const ITEMS_PER_PAGE_MOBILE  = 12
const ITEMS_PER_PAGE_DESKTOP = 24

// ─── Fallback para preview sem imagem ─────────────────────────────────────────
const PreviewFallback = ({ isVideo }: { isVideo: boolean }) => {
  const isDark = useTheme().palette.mode === 'dark'
  return (
    <Box sx={{
      width: '100%', height: '100%',
      background: isVideo
        ? isDark ? 'linear-gradient(135deg, #1a1040 0%, #2d1b5e 100%)' : 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
        : isDark ? 'linear-gradient(135deg, #0d2530 0%, #142830 100%)' : 'linear-gradient(135deg, #e8f5f3 0%, #ccede8 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Box sx={{
        width: 52, height: 52, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: isVideo
          ? isDark ? 'rgba(109,40,217,0.25)' : 'rgba(109,40,217,0.15)'
          : isDark ? 'rgba(20,184,160,0.2)' : 'rgba(20,184,160,0.15)',
        border: `2px solid ${isVideo
          ? isDark ? 'rgba(167,139,250,0.4)' : 'rgba(109,40,217,0.3)'
          : isDark ? 'rgba(20,184,160,0.4)' : 'rgba(13,148,136,0.3)'}`,
      }}>
        {isVideo
          ? <Videocam sx={{ fontSize: 26, color: isDark ? 'rgba(167,139,250,0.8)' : 'rgba(109,40,217,0.6)' }} />
          : <ImageIcon sx={{ fontSize: 26, color: isDark ? 'rgba(20,184,160,0.8)' : 'rgba(13,148,136,0.6)' }} />
        }
      </Box>
    </Box>
  )
}

// ─── Thumbnail com lazy load e fallback ──────────────────────────────────────
const MediaThumb = ({ url, alt, isVideo }: { url: string; alt: string; isVideo: boolean }) => {
  const [loaded, setLoaded] = useState(false)
  const [error, setError]   = useState(false)
  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {!loaded && !error && (
        <Skeleton variant="rectangular" width="100%" height="100%"
          animation="wave" sx={{ position: 'absolute', inset: 0 }} />
      )}
      {!error && url && (
        <Box component="img" src={url} alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(true); setError(true) }}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none' }}
        />
      )}
      {(error || !url) && <PreviewFallback isVideo={isVideo} />}
    </Box>
  )
}

// ─── Card mobile individual ───────────────────────────────────────────────────
type MediaCardProps = {
  item: Media
  isSelected: boolean
  selectionMode: boolean
  onTap: () => void
  onToggleSelect: () => void
  onView: () => void
  onEdit: () => void
  onDelete: () => void
  onAddToPlaylist: () => void
}

const MediaCard = ({
  item, isSelected, selectionMode,
  onTap, onToggleSelect, onView, onEdit, onDelete, onAddToPlaylist,
}: MediaCardProps) => {
  const theme  = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isVideo = item.type.startsWith('video/')
  const bdr = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>

      {/* ── Thumbnail ─────────────────────────────────────── */}
      <Box sx={{
        position: 'relative', borderRadius: '12px', overflow: 'hidden',
        aspectRatio: '1', cursor: 'pointer',
        border: isSelected
          ? `2px solid ${isDark ? '#14b8a0' : '#0d9488'}`
          : `1px solid ${bdr}`,
        boxShadow: isSelected
          ? `0 0 0 3px ${isDark ? 'rgba(20,184,160,0.2)' : 'rgba(13,148,136,0.15)'}`
          : isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        bgcolor: isDark ? '#0f1e26' : '#f0f4f4',
        '&:active': { opacity: 0.85 },
      }}
        onClick={() => selectionMode ? onToggleSelect() : onTap()}
      >
        <MediaThumb url={resolveMediaUrl(item.urlPreview)} alt={item.desc} isVideo={isVideo} />

        {/* Play icon sobre thumbnail de vídeo */}
        {isVideo && !selectionMode && (
          <PlayCircleOutline sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            fontSize: 36, color: 'rgba(255,255,255,0.82)',
            pointerEvents: 'none',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
          }} />
        )}

        {/* Checkbox (modo seleção) */}
        {selectionMode && (
          <Box sx={{ position: 'absolute', top: 5, right: 5 }}>
            <Checkbox
              checked={isSelected} size="small"
              onClick={e => { e.stopPropagation(); onToggleSelect() }}
              sx={{
                p: 0, color: 'white',
                '&.Mui-checked': { color: isDark ? '#14b8a0' : '#0d9488' },
                '& .MuiSvgIcon-root': {
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))',
                  fontSize: 22,
                },
              }}
            />
          </Box>
        )}

        {/* Overlay selecionado */}
        {isSelected && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: isDark ? 'rgba(20,184,160,0.15)' : 'rgba(13,148,136,0.12)', pointerEvents: 'none' }} />
        )}
      </Box>

      {/* ── Nome + botões (fora do card) ──────────────────── */}
      {!selectionMode && (
        <Box sx={{ px: '2px' }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 500,
            color: 'text.primary',
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            lineHeight: 1.35, mb: 0.75, minHeight: '2.7em',
          }}>
            {item.desc}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tooltip title="Visualizar">
              <IconButton size="small" onClick={onView}
                sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}>
                <PlayCircleOutline sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add à Playlist">
              <IconButton size="small" onClick={onAddToPlaylist}
                sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}>
                <PlaylistAdd sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={onEdit}
                sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}>
                <Edit sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={onDelete}
                sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'action.hover' } }}>
                <Delete sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  )
}

// ─── MediasPage ───────────────────────────────────────────────────────────────
type FilterType = 'all' | 'image' | 'video'

const MediasPage = () => {
  const theme   = useTheme()
  const isDark  = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const user      = useAppSelector(state => state.auth.user)
  const medias    = useAppSelector(state => state.media)
  const playlists = useAppSelector(state => state.playlist)
  const dispatch  = useAppDispatch()

  const [selected, setSelected]               = useState<Media | undefined>()
  const [openModalUpdate, setOpenModalUpdate] = useState(false)
  const [openModalUpload, setOpenModalUpload] = useState(false)
  const { sharedFile, clearSharedFile } = useSharedFile()

  useEffect(() => {
    if (sharedFile) setOpenModalUpload(true);
  }, [sharedFile]);
  const [openViewer, setOpenViewer]           = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openHelp, setOpenHelp]               = useState(false)
  const [openAddToPlaylist, setOpenAddToPlaylist] = useState(false)
  const [openQuickPanel, setOpenQuickPanel]   = useState(false)
  const [selectionMode, setSelectionMode]     = useState(false)
  const [selectedIds, setSelectedIds]         = useState<number[]>([])
  const [bulkDeletePending, setBulkDeletePending] = useState<{
    canDelete: number[]
    blocked: { name: string; playlists: string[] }[]
  } | null>(null)
  const [sort, setSort]         = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')

  useFindAllMediaQuery(user?.id?.toString() || '')
  useFindAllPlaylistsQuery(user?.id?.toString() || '')
  const [deleteMedia, { isLoading: isDeleting }] = useDeleteMediaMutation()

  const filteredMedias = filterType === 'all' ? medias
    : filterType === 'image' ? medias.filter(m => m.type.startsWith('image/'))
    : medias.filter(m => m.type.startsWith('video/'))

  const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP
  const { paginatedItems, goToNextPage, goToPreviousPage, canGoNext, canGoPrevious, currentPage } =
    usePagination<Media>({ items: filteredMedias, itemsPerPage })
  const totalPages = Math.ceil(filteredMedias.length / itemsPerPage)

  const imageCount = medias.filter(m => m.type.startsWith('image/')).length
  const videoCount = medias.filter(m => m.type.startsWith('video/')).length

  function handleSortChange(value: string) {
    setSort(value)
    const sorted = [...medias]
    if (value === 'name') sorted.sort((a, b) => a.desc.localeCompare(b.desc))
    else if (value === 'type') sorted.sort((a, b) => a.type.localeCompare(b.type))
    else if (value === 'size') sorted.sort((a, b) => b.size - a.size)
    dispatch(setMedia(sorted))
  }

  function getPlaylistsUsingMedia(media: Media, lists: Playlist[]) {
    return lists
      .filter(pl => pl.items.some(i => (i.type === 'image' || i.type === 'video') && i.data.id === media.id))
      .map(pl => pl.name)
  }

  function handleDeleteClick(item: Media) { setSelected(item); setOpenDeleteDialog(true) }

  async function handleConfirmDelete(ok: boolean) {
    setOpenDeleteDialog(false)
    if (!ok || !selected) return
    const inUse = getPlaylistsUsingMedia(selected, playlists)
    if (inUse.length > 0) { toast.warn(`Em uso nas playlists: ${inUse.join(', ')} — remova lá primeiro`); return }
    await deleteMedia(selected.id).unwrap()
      .then(() => { toast.success('Mídia excluída'); setSelected(undefined) })
      .catch(() => toast.error('Falha ao excluir mídia'))
  }

  function toggleSelect(id: number) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
  }
  function handleSelectAll() {
    setSelectedIds(selectedIds.length === medias.length ? [] : medias.map(m => m.id))
  }
  function exitSelectionMode() { setSelectionMode(false); setSelectedIds([]) }

  function handleBulkDeleteClick() {
    const canDelete: number[] = []
    const blocked: { name: string; playlists: string[] }[] = []
    for (const id of selectedIds) {
      const media = medias.find(m => m.id === id)
      if (!media) continue
      const inUse = getPlaylistsUsingMedia(media, playlists)
      if (inUse.length > 0) blocked.push({ name: media.desc, playlists: inUse })
      else canDelete.push(id)
    }
    setBulkDeletePending({ canDelete, blocked })
  }

  async function handleConfirmBulkDelete(ok: boolean) {
    if (!bulkDeletePending) return
    setBulkDeletePending(null)
    if (!ok) return
    let deleted = 0
    for (const id of bulkDeletePending.canDelete) {
      try { await deleteMedia(id).unwrap(); deleted++ }
      catch { toast.error('Falha ao excluir mídia') }
    }
    if (deleted > 0) toast.success(`${deleted} mídia(s) excluída(s)`)
    if (bulkDeletePending.blocked.length > 0)
      toast.warn(`${bulkDeletePending.blocked.length} mídia(s) ignorada(s) — em uso em playlists`)
    exitSelectionMode()
  }

  function handleClickImage(item: Media) { setSelected(item); setOpenViewer(true) }

  const bdr  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'
  const teal = isDark ? '#14b8a0' : '#0d9488'

  const bulkDialogText = bulkDeletePending
    ? bulkDeletePending.blocked.length > 0
      ? `${bulkDeletePending.canDelete.length} serão excluídas. ${bulkDeletePending.blocked.length} estão em uso e serão ignoradas.`
      : `${bulkDeletePending.canDelete.length} mídia(s) serão excluídas permanentemente.`
    : ''

  // ─── Modais compartilhados ────────────────────────────────────────────────
  const modals = (
    <>
      {selected && (
        <>
          <DialogQuiz title="Confirmar exclusão?"
            text={`"${selected.desc}" será removida permanentemente.`}
            open={openDeleteDialog} handleClose={handleConfirmDelete} variant="warning" />
          <ModalUpdate onClose={() => setOpenModalUpdate(false)} open={openModalUpdate} media={selected} />
          <MediaViewer media={selected} open={openViewer} handleClose={() => setOpenViewer(false)} />
        </>
      )}
      <ModalUpload
        onClose={() => { setOpenModalUpload(false); clearSharedFile(); }}
        open={openModalUpload}
        initialFiles={sharedFile ? [sharedFile] : undefined}
      />
      <ModalAddToPlaylist open={openAddToPlaylist} onClose={() => setOpenAddToPlaylist(false)} media={selected} />
      <PlaylistQuickPanel
        open={openQuickPanel}
        onClose={() => setOpenQuickPanel(false)}
        newMedias={medias.filter(m => selectedIds.includes(m.id))}
        onSaved={exitSelectionMode}
      />
      <DialogQuiz
        title={`Excluir ${bulkDeletePending?.canDelete.length ?? 0} mídia(s)?`}
        text={bulkDialogText}
        open={!!bulkDeletePending} handleClose={handleConfirmBulkDelete} variant="warning" />
      <Modal open={openHelp} onClose={() => setOpenHelp(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          bgcolor: 'background.paper', borderRadius: 2, p: 4,
          maxWidth: 560, width: '90%', maxHeight: '85vh', overflowY: 'auto',
        }}>
          <ModalHeader title="Fotos e Vídeos" onClose={() => setOpenHelp(false)} />
          <Helper />
        </Box>
      </Modal>
    </>
  )

  // ─── Mobile ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {modals}

        {selectionMode ? (
          /* Modo seleção — header contextual */
          <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${bdr}`, display: 'flex', alignItems: 'center', gap: 1, minHeight: 48, flexShrink: 0, bgcolor: 'background.default' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary', flex: 1 }}>
              {selectedIds.length > 0 ? `${selectedIds.length} selecionada(s)` : 'Selecionar'}
            </Typography>
            <Button size="small" variant="contained"
              disabled={!selectedIds.length}
              startIcon={<PlaylistAdd />}
              onClick={() => setOpenQuickPanel(true)} sx={{ height: 32, fontSize: 13 }}>
              Playlist
            </Button>
            <Button size="small" variant="outlined" color="error"
              disabled={!selectedIds.length || isDeleting}
              startIcon={isDeleting ? <CircularProgress size={12} color="inherit" /> : <Delete />}
              onClick={handleBulkDeleteClick} sx={{ height: 32, fontSize: 13 }}>
              {selectedIds.length > 0 ? selectedIds.length : 'Excluir'}
            </Button>
            <Button size="small" variant="outlined" onClick={exitSelectionMode}
              sx={{ height: 32, fontSize: 13, borderColor: bdr, color: 'text.secondary' }}>
              Cancelar
            </Button>
          </Box>
        ) : (
          <AppPageHeader
            title="Fotos e Vídeos"
            count={medias.length}
            helpContent={<Helper />}
            mobileActions={
              medias.length > 0 ? (
                <Tooltip title="Selecionar">
                  <IconButton size="small" onClick={() => setSelectionMode(true)}
                    sx={{ width: 36, height: 36, color: 'text.disabled' }}>
                    <CheckBoxOutlineBlank sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }
          />
        )}

        {/* Filtros tipo + contagens */}
        {!selectionMode && (
          <Box sx={{
            px: 2, py: 1,
            display: 'flex', gap: 0.75, alignItems: 'center',
            flexShrink: 0, borderBottom: `1px solid ${bdr}`,
            bgcolor: 'background.default',
          }}>
            {([
              { key: 'all',   label: `Todos (${medias.length})` },
              { key: 'image', label: `Fotos (${imageCount})` },
              { key: 'video', label: `Vídeos (${videoCount})` },
            ] as { key: FilterType; label: string }[]).map(f => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                onClick={() => setFilterType(f.key)}
                sx={{
                  height: 28, fontSize: 12, fontWeight: filterType === f.key ? 600 : 500,
                  borderRadius: '8px',
                  bgcolor: filterType === f.key
                    ? isDark ? 'rgba(20,184,160,0.2)' : 'rgba(13,148,136,0.12)'
                    : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  color: filterType === f.key ? teal : 'text.secondary',
                  border: filterType === f.key
                    ? `1px solid ${isDark ? 'rgba(20,184,160,0.35)' : 'rgba(13,148,136,0.25)'}`
                    : `1px solid ${bdr}`,
                  '& .MuiChip-label': { px: '10px' },
                  cursor: 'pointer',
                }}
              />
            ))}
          </Box>
        )}

        {/* Galeria */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pt: 1.5, pb: '88px' }}>
          {filteredMedias.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, gap: 1.5 }}>
              <PermMedia sx={{ fontSize: 52, color: 'text.disabled', opacity: 0.35 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
                {filterType !== 'all' ? 'Nenhuma mídia neste filtro' : 'Nenhuma mídia enviada'}
              </Typography>
              {filterType === 'all' && (
                <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', px: 4 }}>
                  Toque no botão + para fazer upload de fotos e vídeos
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
              }}>
                {paginatedItems.map(item => (
                  <MediaCard
                    key={item.id}
                    item={item}
                    isSelected={selectedIds.includes(item.id)}
                    selectionMode={selectionMode}
                    onTap={() => handleClickImage(item)}
                    onToggleSelect={() => toggleSelect(item.id)}
                    onView={() => { setSelected(item); setOpenViewer(true) }}
                    onEdit={() => { setSelected(item); setOpenModalUpdate(true) }}
                    onDelete={() => handleDeleteClick(item)}
                    onAddToPlaylist={() => { setSelected(item); setOpenAddToPlaylist(true) }}
                  />
                ))}
              </Box>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2.5, mb: 1 }}>
                  <IconButton onClick={goToPreviousPage} disabled={!canGoPrevious}
                    sx={{ width: 40, height: 40, border: `1px solid ${bdr}` }}>
                    <Typography sx={{ fontSize: 18, lineHeight: 1 }}>‹</Typography>
                  </IconButton>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {currentPage + 1} / {totalPages}
                  </Typography>
                  <IconButton onClick={goToNextPage} disabled={!canGoNext}
                    sx={{ width: 40, height: 40, border: `1px solid ${bdr}` }}>
                    <Typography sx={{ fontSize: 18, lineHeight: 1 }}>›</Typography>
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* FAB upload */}
        {!selectionMode && (
          <Fab onClick={() => setOpenModalUpload(true)} sx={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom) + 24px)',
            right: 24,
            background: `linear-gradient(135deg, ${teal} 0%, #0d9488 100%)`,
            color: '#fff',
            boxShadow: '0 6px 20px rgba(20,184,160,0.4)',
            '&:hover': { background: 'linear-gradient(135deg, #0d9488 0%, #0a7a70 100%)' },
            '&:active': { transform: 'scale(0.94)' },
            transition: 'all 0.2s ease',
          }}>
            <Upload />
          </Fab>
        )}
      </Box>
    )
  }

  // ─── Desktop ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {modals}

      {selectionMode ? (
        <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 56, flexShrink: 0 }}>
          <Typography variant="subtitle1" fontWeight={500} noWrap sx={{ flexShrink: 0 }}>
            {selectedIds.length > 0 ? `${selectedIds.length} selecionada(s)` : 'Selecionar'}
          </Typography>
          <Button size="small" onClick={handleSelectAll} sx={{ flexShrink: 0 }}
            startIcon={selectedIds.length === medias.length ? <CheckBox /> : <CheckBoxOutlineBlank />}>
            {selectedIds.length === medias.length ? 'Desmarcar tudo' : 'Tudo'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" sx={{ flexShrink: 0 }}
            disabled={!selectedIds.length}
            startIcon={<PlaylistAdd />}
            onClick={() => setOpenQuickPanel(true)}>
            {`Adicionar à playlist${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
          </Button>
          <Button size="small" variant="outlined" color="error" sx={{ flexShrink: 0 }}
            disabled={!selectedIds.length || isDeleting}
            startIcon={isDeleting ? <CircularProgress size={13} color="inherit" /> : <Delete />}
            onClick={handleBulkDeleteClick}>
            {`Excluir${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
          </Button>
          <Button size="small" variant="outlined" onClick={exitSelectionMode}
            sx={{ flexShrink: 0, color: 'text.secondary', borderColor: 'divider' }}>
            Cancelar
          </Button>
        </Box>
      ) : (
        <AppPageHeader
          title="Fotos e Vídeos"
          count={medias.length}
          helpContent={<Helper />}
          actions={
            <>
              <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
                <InputLabel>Ordenar</InputLabel>
                <Select label="Ordenar" value={sort} onChange={e => handleSortChange(e.target.value)}>
                  <MenuItem value="name">Nome</MenuItem>
                  <MenuItem value="type">Tipo</MenuItem>
                  <MenuItem value="size">Tamanho</MenuItem>
                </Select>
              </FormControl>
              {medias.length > 0 && (
                <Tooltip title="Selecionar mídias">
                  <IconButton size="small" onClick={() => setSelectionMode(true)}
                    sx={{ flexShrink: 0, border: 1, borderColor: 'divider', borderRadius: 1, width: 36, height: 36 }}>
                    <CheckBoxOutlineBlank fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <TealButton size="md" onClick={() => setOpenModalUpload(true)} startIcon={<Upload />}>
                Upload
              </TealButton>
            </>
          }
        />
      )}

      {/* Galeria desktop */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {medias.length === 0 ? (
          <EmptyState icon={PermMedia} title="Nenhuma mídia enviada"
            subtitle='Clique em "Upload" para adicionar fotos e vídeos' />
        ) : (
          <>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 2,
            }}>
              {paginatedItems.map(item => {
                const isSelected = selectedIds.includes(item.id)
                const isVideo    = item.type.startsWith('video/')
                return (
                  <Box key={item.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>

                    {/* Thumbnail */}
                    <Box
                      onClick={() => selectionMode ? toggleSelect(item.id) : handleClickImage(item)}
                      sx={{
                        position: 'relative', borderRadius: 2, overflow: 'hidden',
                        aspectRatio: '1', bgcolor: isDark ? '#0f1e26' : '#f0f4f4',
                        cursor: 'pointer',
                        border: isSelected
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.divider}`,
                        transition: 'border-color 0.15s, transform 0.15s',
                        '&:hover': { transform: 'scale(1.02)' },
                      }}>
                      <MediaThumb url={resolveMediaUrl(item.urlPreview)} alt={item.desc} isVideo={isVideo} />
                      {isVideo && (
                        <PlayCircleOutline sx={{
                          position: 'absolute', top: '50%', left: '50%',
                          transform: 'translate(-50%,-50%)',
                          fontSize: 40, color: 'rgba(255,255,255,0.85)', pointerEvents: 'none',
                        }} />
                      )}
                      {isSelected && (
                        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'primary.main', opacity: 0.18, pointerEvents: 'none' }} />
                      )}
                      {selectionMode && (
                        <Checkbox checked={isSelected} size="small" sx={{
                          position: 'absolute', top: 2, right: 2, color: 'white', p: 0.5,
                          '&.Mui-checked': { color: 'primary.main' },
                        }} />
                      )}
                    </Box>

                    {/* Nome + botões fora do card */}
                    {!selectionMode && (
                      <Box sx={{ px: '2px' }}>
                        <Typography sx={{
                          fontSize: 12, fontWeight: 500, color: 'text.primary',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          lineHeight: 1.35, mb: 0.75, minHeight: '2.7em',
                        }}>
                          {item.desc}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Tooltip title="Visualizar">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleClickImage(item) }}
                              sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}>
                              <PlayCircleOutline sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add à Playlist">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); setSelected(item); setOpenAddToPlaylist(true) }}
                              sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}>
                              <PlaylistAdd sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); setSelected(item); setOpenModalUpdate(true) }}
                              sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}>
                              <Edit sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteClick(item) }}
                              sx={{ p: '5px', borderRadius: '8px', color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'action.hover' } }}>
                              <Delete sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )
              })}
            </Box>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
                <Button size="small" variant="outlined" onClick={goToPreviousPage}
                  disabled={!canGoPrevious} sx={{ minWidth: 36, height: 36, p: 0 }}>‹</Button>
                <Typography variant="body2" color="text.secondary">
                  {currentPage + 1} / {totalPages}
                </Typography>
                <Button size="small" variant="outlined" onClick={goToNextPage}
                  disabled={!canGoNext} sx={{ minWidth: 36, height: 36, p: 0 }}>›</Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default MediasPage
